import { getHTMLElement, getRootElement, isPromise } from "../utility/helper";
import { AnimationSequencesType } from "../utility/constants/animations";

export interface AnimationStartsCallbacks {
  onAnimationStarts?: (() => Promise<unknown>) | (() => void);
}

export interface AnimationEndsCallbacks {
  onAnimationEnds?: (() => Promise<unknown>) | (() => void);
}

export type AnimationStatusCallbacks = AnimationStartsCallbacks &
  AnimationEndsCallbacks;

// eslint-disable-next-line @typescript-eslint/ban-types
export type SequenceIdentifier = AnimationSequencesType | (string & {});

export interface NaviAnimation {
  animation: AnimationGeneratorType;
  elementSelector: string;
  onStart?: (() => Promise<unknown>) | (() => unknown);
  onEnd?: (() => Promise<unknown>) | (() => unknown);
}

export type SequenceStep = NaviAnimation[];

export interface AnimationSequence {
  steps: SequenceStep[];
  currentStepIndex: number;
  running: boolean;
  resolve?: (value: unknown) => void;
  reject?: (reason?: unknown) => void;
  promise?: Promise<void>;
}

export interface AnimationGeneratorType {
  className: string;
  cleanUpBeforeAnimation?:
    | ((el: HTMLElement) => Promise<void>)
    | ((el: HTMLElement) => void)
    | boolean;
  cleanUpAfterAnimation?:
    | ((el: HTMLElement) => Promise<void>)
    | ((el: HTMLElement) => void)
    | boolean;
}

const animationRegistry: Record<
  string,
  {
    promise: Promise<void>;
    resolve: (value: void) => void;
    reject: (reason?: unknown) => void;
    animation: AnimationGeneratorType;
    timeout: NodeJS.Timeout;
  }
> = {};

const sequences: {
  [key in SequenceIdentifier]?: AnimationSequence | undefined;
} = {};

const animationTimeoutDurationMs = 700;

function initialize(): void {
  getRootElement().addEventListener("transitionend", animationEndListener);
  getRootElement().addEventListener("animationend", animationEndListener);
}

function register(
  sequenceIdentifier: SequenceIdentifier,
  animationSequence: AnimationSequence,
): void {
  sequences[sequenceIdentifier] = animationSequence;
}

function reset(): void {
  Object.keys(sequences).forEach((key) => delete sequences[key]);
}

async function play(sequenceIdentifier: SequenceIdentifier): Promise<unknown> {
  const header =
    document.body.querySelector("phn-header")?.shadowRoot ??
    document.body.querySelector(".phn-header");

  if (
    (sequenceIdentifier.toLowerCase().includes("singledrawer") &&
      !header?.querySelector("phn-nd-single-drawer")) ||
    (sequenceIdentifier.toLowerCase().includes("doubledrawer") &&
      !header?.querySelector("phn-nd-double-drawer"))
  )
    return;

  const sequence = sequences[sequenceIdentifier];

  if (sequence === undefined) {
    // eslint-disable-next-line consistent-return
    return new Promise((_, reject) =>
      reject(
        new Error(`Sequence ${sequenceIdentifier} has not been registered`),
      ),
    );
  }

  if (!sequence.running) {
    sequence.promise = new Promise<void>((resolve, reject) => {
      sequence.resolve = () => {
        sequence.running = false;
        resolve();
      };

      sequence.reject = (value: unknown) => {
        sequence.running = false;
        reject(value);
      };

      sequence.running = true;

      applyCurrentAnimationSequenceStep(sequence);
    });
  }

  // eslint-disable-next-line consistent-return
  return sequence.promise;
}

async function applyCurrentAnimationSequenceStep(
  sequence: AnimationSequence,
): Promise<void> {
  if (sequence.currentStepIndex >= sequence.steps.length) {
    finishAnimationSequence(sequence);
    return;
  }

  const sequenceStep = sequence.steps[sequence.currentStepIndex];

  const promises = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const animation of sequenceStep) {
    promises.push(executeAnimation(animation));
  }

  try {
    await Promise.all(promises);
  } catch (error) {
    sequence.reject?.(error);
    return;
  }

  sequence.currentStepIndex += 1;

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  setTimeout(async () => applyCurrentAnimationSequenceStep(sequence), 0);
}

function finishAnimationSequence(sequence: AnimationSequence): void {
  sequence.currentStepIndex = 0;
  sequence.resolve?.(null);
  sequence.promise = undefined;
}

function animationEndListener(event: AnimationEvent | TransitionEvent): void {
  const target = getHTMLElement(event.target);

  if (target === null) {
    throw new Error(`${event.type} event target is null`);
  }

  const id = target.dataset.phnAmId;

  if (id !== undefined && id !== null) {
    finishAnimationPromise(id, target);
  }
}

async function executeAnimation({
  onStart,
  onEnd,
  animation,
  elementSelector,
}: NaviAnimation): Promise<void> {
  if (onStart) {
    const maybePromise = onStart();
    if (isPromise<void>(maybePromise)) {
      await maybePromise;
    }
  }

  const animatedElements: HTMLElement[] = Array.from(
    getRootElement().querySelectorAll(elementSelector),
  );
  const animationExecutors = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const element of animatedElements) {
    const animationPromiseEffect = generateAndCleanUpAnimationPromise(
      animationPromiseGenerator,
    )(element, animation);
    animationExecutors.push(animationPromiseEffect);
  }

  await Promise.all(animationExecutors);
  if (onEnd) {
    const maybePromise = onEnd();
    if (isPromise<void>(maybePromise)) {
      await maybePromise;
    }
  }
}

function generateAndCleanUpAnimationPromise(
  animationPromiseGenerator: (
    element: HTMLElement,
    animation: AnimationGeneratorType,
  ) => Promise<void>,
): (element: HTMLElement, animation: AnimationGeneratorType) => Promise<void> {
  return async function (
    element: HTMLElement,
    animation: AnimationGeneratorType,
  ): Promise<void> {
    const { cleanUpBeforeAnimation, cleanUpAfterAnimation } = animation;

    const animationPromise = animationPromiseGenerator(element, animation);

    if (typeof cleanUpBeforeAnimation === "function") {
      const maybePromise: void | Promise<void> =
        cleanUpBeforeAnimation(element);
      if (isPromise<void>(maybePromise)) {
        await maybePromise;
      }
    }

    await animationPromise;

    if (typeof cleanUpAfterAnimation === "function") {
      const maybePromise: void | Promise<void> = cleanUpAfterAnimation(element);
      if (isPromise<void>(maybePromise)) {
        await maybePromise;
      }
    }

    if (cleanUpAfterAnimation !== false) {
      // only not remove class if cleanUpAfterAnimation is explicitely set to false
      element.classList.remove(animation.className);
    }
  };
}

const animationPromiseGenerator = async (
  element: HTMLElement,
  animation: AnimationGeneratorType,
): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  const id = Math.floor(Math.random() * 1e7);

  element.classList.add(animation.className);
  element.dataset.phnAmId = id.toString();

  let resolve!: (value: void) => void;
  let reject!: (reason?: unknown) => void;
  let timeout!: NodeJS.Timeout;
  const promise = new Promise<void>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;

    timeout = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn(
        `Animation with className ${animation.className} didn't finish after ${animationTimeoutDurationMs}ms. Forcefully finishing animation and continuing with the rest of the sequence.`,
      );
      finishAnimationPromise(String(id), element);
    }, animationTimeoutDurationMs);
  });

  animationRegistry[id] = { animation, resolve, reject, promise, timeout };

  return promise;
};

function finishAnimationPromise(phnAmId: string, target: HTMLElement): void {
  const { resolve, timeout } = animationRegistry[phnAmId];

  clearTimeout(timeout);
  delete animationRegistry[phnAmId];
  delete target.dataset.phnAmId;

  resolve();
}

function isPlaying(sequenceIdentifier: SequenceIdentifier): boolean {
  const sequence = sequences[sequenceIdentifier];

  if (sequence === undefined) {
    throw new Error(`Sequence ${sequenceIdentifier} has not been registered`);
  }

  return sequence.promise !== undefined;
}

function isPlayingAny(): boolean {
  return Object.values(sequences).some(
    (sequence) => sequence?.promise !== undefined,
  );
}

function makeSequence(
  sequenceSteps: SequenceStep[],
  animationDefaults?: Partial<Omit<AnimationGeneratorType, "name">>,
): AnimationSequence {
  return {
    steps: sequenceSteps.map((step) =>
      step.map((animation) => {
        return { ...animationDefaults, ...animation };
      }),
    ),
    currentStepIndex: 0,
    running: false,
  };
}

export default {
  initialize,
  play,
  isPlaying,
  register,
  reset,
  isPlayingAny,
};

export { makeSequence };
